import React, { useCallback, useEffect, useState } from 'react';
import {
  Pencil1Icon,
  PlusIcon,
  ReaderIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Button, Input, Spinner } from '../../../../components';
import { useAuthStore } from '../../../../store/authStore';
import {
  canModifyNote,
  formatNoteDate,
  internalNotesService,
  NOTE_CONTENT_MAX,
  NOTE_TITLE_MAX,
} from '../../../../services/internalNotesService';
import type { InternalNote } from '../../../../types/internalNote';
import './InternalNotesTab.scss';

interface InternalNotesTabProps {
  clientUserId: string;
}

const mapNoteError = (err: unknown): string => {
  const message = (err as { message?: string })?.message ?? '';
  if (message.includes('only modify notes you created')) {
    return 'You can only edit or delete your own notes.';
  }
  if (message.includes('permission')) {
    return "You don't have access to this action.";
  }
  return message || 'Something went wrong';
};

export const InternalNotesTab: React.FC<InternalNotesTabProps> = ({
  clientUserId,
}) => {
  const staffUserId = useAuthStore((state) => state.staffUserId);
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const canCreate = hasPermission('user_mgmt:create');
  const canUpdate = hasPermission('user_mgmt:update');
  const canDelete = hasPermission('user_mgmt:delete');

  const resetForm = useCallback(() => {
    setTitle('');
    setContent('');
    setEditingNoteId(null);
    setIsFormOpen(false);
  }, []);

  const loadNotes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await internalNotesService.listNotes(clientUserId);
      setNotes(rows);
    } catch (err) {
      setError(mapNoteError(err));
    } finally {
      setIsLoading(false);
    }
  }, [clientUserId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleStartCreate = useCallback(() => {
    setEditingNoteId(null);
    setTitle('');
    setContent('');
    setIsFormOpen(true);
  }, []);

  const handleEdit = useCallback((note: InternalNote) => {
    setEditingNoteId(String(note.internal_note_id));
    setTitle(note.title);
    setContent(note.content);
    setIsFormOpen(true);
  }, []);

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const trimmedTitle = title.trim();
      const trimmedContent = content.trim();
      if (!trimmedTitle || !trimmedContent) return;

      setIsSaving(true);
      setError(null);
      try {
        if (editingNoteId) {
          const updated = await internalNotesService.updateNote(
            clientUserId,
            editingNoteId,
            { title: trimmedTitle, content: trimmedContent }
          );
          setNotes((prev) =>
            prev.map((note) =>
              String(note.internal_note_id) === editingNoteId ? updated : note
            )
          );
        } else {
          const created = await internalNotesService.createNote(clientUserId, {
            title: trimmedTitle,
            content: trimmedContent,
          });
          setNotes((prev) => [created, ...prev]);
        }
        resetForm();
      } catch (err) {
        setError(mapNoteError(err));
      } finally {
        setIsSaving(false);
      }
    },
    [clientUserId, content, editingNoteId, resetForm, title]
  );

  const handleDelete = useCallback(
    async (noteId: string) => {
      if (!window.confirm('Delete this note?')) return;
      setError(null);
      try {
        await internalNotesService.deleteNote(clientUserId, noteId);
        setNotes((prev) =>
          prev.filter((note) => String(note.internal_note_id) !== noteId)
        );
        if (editingNoteId === noteId) resetForm();
      } catch (err) {
        setError(mapNoteError(err));
      }
    },
    [clientUserId, editingNoteId, resetForm]
  );

  const isFormValid =
    title.trim().length > 0 &&
    title.trim().length <= NOTE_TITLE_MAX &&
    content.trim().length > 0 &&
    content.trim().length <= NOTE_CONTENT_MAX;

  return (
    <section className="internal-notes-tab">
      <div className="internal-notes-tab__header">
        <h2>
          <ReaderIcon aria-hidden="true" />
          Internal Notes
        </h2>
        {!isFormOpen && canCreate && (
          <Button
            size="small"
            className="internal-notes-tab__create-btn"
            onClick={handleStartCreate}
          >
            <PlusIcon aria-hidden="true" />
            Create Note
          </Button>
        )}
      </div>

      {error && <p className="internal-notes-tab__error">{error}</p>}

      {isFormOpen && (
        <form className="internal-notes-tab__form" onSubmit={handleSave}>
          <Input
            label="Note Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="e.g., Preference Update"
            maxLength={NOTE_TITLE_MAX}
          />
          <div className="internal-notes-tab__content-field">
            <label htmlFor="internal-note-content">Note Content</label>
            <textarea
              id="internal-note-content"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Write your internal note here..."
              rows={4}
              maxLength={NOTE_CONTENT_MAX}
            />
          </div>
          <div className="internal-notes-tab__form-actions">
            <button
              type="button"
              className="internal-notes-tab__cancel"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="internal-notes-tab__save"
              disabled={isSaving || !isFormValid}
            >
              {isSaving ? 'Saving…' : editingNoteId ? 'Save Changes' : 'Save Note'}
            </button>
          </div>
        </form>
      )}

      <div className="internal-notes-tab__table-wrap">
        {isLoading ? (
          <div className="internal-notes-tab__loading">
            <Spinner size="medium" />
          </div>
        ) : (
          <table className="internal-notes-tab__table">
            <thead>
              <tr>
                <th>Note Title</th>
                <th>Created By</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="internal-notes-tab__empty">
                    No internal notes yet.
                  </td>
                </tr>
              ) : (
                notes.map((note) => {
                  const noteId = String(note.internal_note_id);
                  const showEdit =
                    canUpdate && canModifyNote(note, staffUserId ?? undefined);
                  const showDelete =
                    canDelete && canModifyNote(note, staffUserId ?? undefined);

                  return (
                    <tr key={noteId}>
                      <td className="internal-notes-tab__title-cell">
                        <strong>{note.title}</strong>
                      </td>
                      <td>{note.created_by?.name ?? '—'}</td>
                      <td>{formatNoteDate(note.created_at)}</td>
                      <td>
                        {(showEdit || showDelete) && (
                          <div className="internal-notes-tab__actions">
                            {showEdit && (
                              <button
                                type="button"
                                aria-label={`Edit ${note.title}`}
                                onClick={() => handleEdit(note)}
                              >
                                <Pencil1Icon aria-hidden="true" />
                              </button>
                            )}
                            {showDelete && (
                              <button
                                type="button"
                                aria-label={`Delete ${note.title}`}
                                onClick={() => handleDelete(noteId)}
                              >
                                <TrashIcon aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};
